import {
  type Mesh,
  type Object3D,
  type PerspectiveCamera,
  EventDispatcher,
  Sphere,
  Vector3,
} from '@seddi/three';
import { type GarmentGeometryGfx, filterMeshes } from '@seddi/ui-three-viewer';

import type { MockupLogo } from '../types/mockupLogos';
import { type FrameBounds, animateCamera, createFromBounds } from './frameLookAt';
import { computeFrameBounds as computeFrameBoundsFromGeometry } from './frameGarmentGeometryRegion';
import { computeFrameBounds as computeFrameBoundsFromMesh } from './frameMeshRegion';
import type { LogoRegion } from './viewerScene';

const _endEvent: { type: 'end' } = { type: 'end' };

type EventMap = { end: {} };

export class ViewerFrameControls extends EventDispatcher<EventMap> {
  readonly camera: PerspectiveCamera;
  readonly element: HTMLElement;

  enabled = true;
  moveSpeed = 0.05;
  rotateSpeed = 0.18;

  private readonly frame_: FrameBounds;
  private readonly regionsBounds: Map<string, FrameBounds>;
  private readonly sceneBounds: FrameBounds;

  private geometry: GarmentGeometryGfx | undefined;
  private regions: Readonly<LogoRegion[]>;
  private readonly models: Map<string, Mesh>;

  constructor(camera: PerspectiveCamera, element: HTMLCanvasElement) {
    super();
    this.camera = camera;
    this.element = element;

    this.frame_ = {
      normal: new Vector3(0, 0, 1),
      sphere: new Sphere(new Vector3(0, 0, 0), 1),
    };
    this.regions = [];
    this.regionsBounds = new Map();
    this.sceneBounds = {
      normal: new Vector3(0, 0, 1),
      sphere: new Sphere(new Vector3(0, 0, 0), 1),
    };
    this.models = new Map();
  }

  get frame() {
    return this.frame_;
  }

  set frame(value: FrameBounds) {
    this.frame_.normal.copy(value.normal);
    this.frame_.sphere.copy(value.sphere);
  }

  get target(): Vector3 | undefined {
    return this.frame_.sphere.center;
  }

  dispose() {}

  updateRegions(regions: Readonly<LogoRegion[]>) {
    this.regions = regions;
    this.regionsBounds.clear();
    this.buildGarmentGeometry();
    this.buildModels();
  }

  updateGarmentGeometry(geometry: GarmentGeometryGfx) {
    this.geometry = geometry;
    this.buildGarmentGeometry();
  }

  updateModel(model: Object3D) {
    const meshes = filterMeshes(model);
    for (const mesh of meshes) {
      const name = ((mesh.userData as { name?: string })?.name ?? mesh.name) as string;
      this.models.set(name, mesh);
    }
    this.buildModels();
  }

  /**
   * Update selection.
   * @param logos Logos.
   * @param selection Selection.
   */
  updateSelection(logos: MockupLogo[], selection: string | undefined) {
    this.frame = this.sceneBounds;
    if (!selection) return;
    const logo = logos.find(l => l.id === selection);
    if (!logo) return;
    const frame = this.regionsBounds.get(logo.region);
    if (!frame) return;
    this.frame = frame;
  }

  update() {
    if (!this.enabled) return;

    const target = { distance: 0, origin: new Vector3(), pivot: new Vector3() };
    createFromBounds(this.camera, this.frame_, target);
    const isDone = animateCamera(this.camera, target, this.moveSpeed, this.rotateSpeed);
    if (isDone) {
      this.dispatchEvent(_endEvent);
    }
  }

  private buildGarmentGeometry() {
    const { geometry, regions, regionsBounds } = this;
    if (!geometry) return;

    for (const region of regions) {
      const { entityId, id } = region;
      if (regionsBounds.has(id)) continue;

      const piece = geometry.pieces.get(entityId);
      if (!piece) continue;

      const frame = computeFrameBoundsFromGeometry(region, piece);
      regionsBounds.set(id, frame);
    }
  }

  private buildModels() {
    for (const [, mesh] of this.models) {
      this.buildModelMesh(mesh);
    }
  }

  private buildModelMesh(mesh: Mesh) {
    const { regions, regionsBounds } = this;
    const name = ((mesh.userData as { name?: string })?.name ?? mesh.name) as string;
    const subset = regions.filter(r => r.pieceName === name);

    for (const region of subset) {
      if (regionsBounds.has(region.id)) continue;
      const frame = computeFrameBoundsFromMesh(region, mesh);
      regionsBounds.set(region.id, frame);
    }
  }
}
